import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

interface ColumnMapping {
  column_name: string;
  field_name: string;
  required: boolean;
  transform: string | null;
  matched?: boolean; // Whether this mapping found a match
  csv_column?: string; // The actual CSV column that was matched
}

interface ColumnMappingDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (mappings: ColumnMapping[]) => void;
  presetMappings: ColumnMapping[];
  csvColumns: string[];
  unmatchedColumns: string[];
  presetName: string;
}

export const ColumnMappingDialog: React.FC<ColumnMappingDialogProps> = ({
  open,
  onClose,
  onConfirm,
  presetMappings,
  csvColumns,
  unmatchedColumns,
  presetName,
}) => {
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (open) {
      // Initialize mappings from preset
      setMappings(presetMappings.map(m => ({
        ...m,
        matched: m.csv_column !== undefined,
        csv_column: m.csv_column || ''
      })));
      setHasChanges(false);
    }
  }, [open, presetMappings]);

  const handleColumnChange = (mappingIndex: number, csvColumn: string) => {
    const updated = [...mappings];
    updated[mappingIndex] = {
      ...updated[mappingIndex],
      csv_column: csvColumn,
      matched: csvColumn !== '' && csvColumn !== 'none'
    };
    setMappings(updated);
    setHasChanges(true);
  };

  const getMatchedCount = () => {
    return mappings.filter(m => m.csv_column && m.csv_column !== 'none').length;
  };

  const getRequiredUnmatched = () => {
    return mappings.filter(m => m.required && (!m.csv_column || m.csv_column === 'none'));
  };

  const handleConfirm = () => {
    // Filter out mappings with no CSV column selected
    const validMappings = mappings
      .filter(m => m.csv_column && m.csv_column !== 'none')
      .map(m => ({
        column_name: m.csv_column!, // Use the selected CSV column name
        field_name: m.field_name,
        required: m.required,
        transform: m.transform
      }));
    
    onConfirm(validMappings);
  };

  const requiredUnmatched = getRequiredUnmatched();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Map CSV Columns - {presetName} Preset</DialogTitle>
          <DialogDescription>
            Some columns from the {presetName} preset couldn't be automatically matched. 
            Please map them to your CSV columns below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Summary */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <span>
                  {getMatchedCount()} of {mappings.length} columns mapped
                </span>
                {unmatchedColumns.length > 0 && (
                  <Badge variant="destructive">
                    {unmatchedColumns.length} unmatched
                  </Badge>
                )}
              </div>
            </AlertDescription>
          </Alert>

          {/* Required fields warning */}
          {requiredUnmatched.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Required fields missing:</strong> {requiredUnmatched.map(m => m.column_name).join(', ')}
                <br />
                These fields must be mapped to continue.
              </AlertDescription>
            </Alert>
          )}

          {/* Column mappings */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-gray-700 mb-2">
              Column Mappings
            </div>
            {mappings.map((mapping, index) => {
              const isMatched = mapping.csv_column && mapping.csv_column !== 'none';
              const isRequired = mapping.required;
              
              return (
                <div
                  key={index}
                  className={`p-3 border rounded-lg ${
                    isMatched ? 'bg-green-50 border-green-200' : 
                    isRequired ? 'bg-red-50 border-red-200' : 
                    'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Label className="font-medium">
                          {mapping.column_name}
                        </Label>
                        {isRequired && (
                          <Badge variant="outline" className="text-xs">
                            Required
                          </Badge>
                        )}
                        {isMatched && (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        )}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        Maps to: <code className="bg-gray-100 px-1 rounded">{mapping.field_name}</code>
                        {mapping.transform && (
                          <span className="ml-2 text-blue-600">
                            (transforms: {mapping.transform})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Select
                    value={mapping.csv_column || 'none'}
                    onValueChange={(value) => handleColumnChange(index, value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select CSV column..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- No mapping --</SelectItem>
                      {csvColumns.map((col) => (
                        <SelectItem key={col} value={col}>
                          {col}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>

          {/* Available CSV columns info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-sm font-medium text-blue-900 mb-1">
              Available CSV Columns ({csvColumns.length})
            </div>
            <div className="text-xs text-blue-800">
              {csvColumns.join(', ')}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={requiredUnmatched.length > 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {requiredUnmatched.length > 0
              ? `Map ${requiredUnmatched.length} Required Field(s)`
              : 'Continue with Mappings'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
